import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PaymentForm = ({ onSubmit, config }) => {
  const [selectedCrypto, setSelectedCrypto] = useState('usdc');
  const [amount, setAmount] = useState('');
  const [usdAmount, setUsdAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [errors, setErrors] = useState({});

  const cryptoAssets = [
    { 
      id: 'btc', 
      name: 'Bitcoin', 
      symbol: 'BTC', 
      icon: 'Bitcoin',
      rate: 45000,
      color: '#F7931A'
    },
    { 
      id: 'eth', 
      name: 'Ethereum', 
      symbol: 'ETH', 
      icon: 'Coins',
      rate: 2420,
      color: '#627EEA'
    },
    { 
      id: 'usdc', 
      name: 'USD Coin', 
      symbol: 'USDC', 
      icon: 'DollarSign',
      rate: 1,
      color: '#2775CA'
    },
    { 
      id: 'usdt', 
      name: 'Tether', 
      symbol: 'USDT', 
      icon: 'DollarSign',
      rate: 1,
      color: '#26A17B'
    }
  ];

  const cryptoOptions = cryptoAssets?.map(asset => ({
    value: asset?.id,
    label: `${asset?.name} (${asset?.symbol})`,
    description: `1 ${asset?.symbol} = $${asset?.rate?.toLocaleString()}`
  }));

  const selectedAsset = cryptoAssets?.find(a => a?.id === selectedCrypto);

  useEffect(() => {
    if (usdAmount && selectedAsset) {
      const cryptoAmount = (parseFloat(usdAmount) / selectedAsset?.rate)?.toFixed(8);
      setAmount(cryptoAmount);
    }
  }, [usdAmount, selectedAsset]);

  useEffect(() => {
    if (amount && selectedAsset) {
      const usd = (parseFloat(amount) * selectedAsset?.rate)?.toFixed(2);
      setUsdAmount(usd);
    }
  }, [amount, selectedAsset]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    
    if (!walletAddress || walletAddress?.length < 26) {
      newErrors.walletAddress = 'Please enter a valid wallet address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        selectedCrypto,
        amount,
        usdAmount,
        walletAddress,
        merchantId: config?.merchantId || 'demo_merchant'
      });
    }
  };

  const generateQRCode = () => {
    setShowQR(!showQR);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Merchant Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        {config?.merchantLogo ? (
          <img src={config?.merchantLogo} alt={config?.merchantName} className="w-10 h-10 rounded-lg" />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Store" size={20} className="text-primary" />
          </div>
        )}
        <div>
          <h3 className="font-semibold text-foreground">{config?.merchantName}</h3>
          <p className="text-xs text-muted-foreground">Secure crypto checkout</p>
        </div>
      </div>

      {/* Cryptocurrency Selection */}
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Select Cryptocurrency
        </label>
        <Select
          options={cryptoOptions}
          value={selectedCrypto}
          onChange={setSelectedCrypto}
          placeholder="Choose payment method"
        />
      </div>

      {/* Amount Input */}
      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-muted/20 border border-border">
          <label className="text-xs text-muted-foreground mb-2 block">Amount in USD</label>
          <div className="flex items-center gap-2">
            <span className="text-2xl text-muted-foreground">$</span>
            <input
              type="number"
              value={usdAmount}
              onChange={(e) => setUsdAmount(e?.target?.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="flex-1 text-2xl font-bold bg-transparent border-0 outline-none text-foreground"
            />
          </div>
          {errors?.amount && (
            <p className="text-xs text-error mt-2">{errors?.amount}</p>
          )}
        </div>

        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
          <label className="text-xs text-muted-foreground mb-2 block">
            You pay in {selectedAsset?.symbol}
          </label>
          <div className="flex items-center gap-2">
            <Icon name={selectedAsset?.icon} size={20} style={{ color: selectedAsset?.color }} />
            <span className="text-2xl font-bold text-foreground">
              {amount || '0.00'} {selectedAsset?.symbol}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Rate: 1 {selectedAsset?.symbol} = ${selectedAsset?.rate?.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Wallet Address */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-foreground">
            Your Wallet Address
          </label>
          {config?.enableQR && (
            <button
              type="button"
              onClick={generateQRCode}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              <Icon name="QrCode" size={14} />
              {showQR ? 'Hide QR' : 'Show QR'}
            </button>
          )}
        </div>
        <Input
          type="text"
          value={walletAddress}
          onChange={(e) => setWalletAddress(e?.target?.value)}
          placeholder="Enter your wallet address"
          error={errors?.walletAddress}
        />
        {showQR && walletAddress && (
          <div className="mt-3 p-4 bg-white rounded-lg border border-border flex justify-center">
            <div className="w-32 h-32 bg-muted flex items-center justify-center">
              <Icon name="QrCode" size={64} className="text-muted-foreground" />
            </div>
          </div>
        )}
      </div>

      {/* Rate Lock Notice */}
      {config?.enableRateLock && (
        <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <Icon name="Lock" size={16} className="text-warning mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-medium text-foreground">Rate Lock Active</p>
            <p className="text-xs text-muted-foreground">Exchange rate locked for 10 minutes</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        iconName="ArrowRight"
        iconPosition="right"
        style={{ backgroundColor: config?.brandColor }}
      >
        Continue to Payment
      </Button>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Icon name="Shield" size={14} />
        <span>Secured by BOLTPay • PCI DSS Compliant</span>
      </div>
    </form>
  );
};

export default PaymentForm;